import { useAuth0 } from '@auth0/auth0-react';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useRedisWritesBlocked } from '@/apis/call';
import { updateAssigneeForList, updateList } from '@/apis/lists';
import { microfrontendURLPrefix } from '@/config';
import { SupporterList } from '@/types';
import { AgDialog, useSaladBar } from '@australiangreens/ag-internal-components';
import { useNavigate } from 'react-router';

const getUserRoleForList = (userIsCoordinator: boolean, userIsCaller: boolean) => {
  if (userIsCoordinator && userIsCaller) return 'Both';
  if (userIsCoordinator) return 'Coordinator';
  if (userIsCaller) return 'Caller';
  return 'None';
};

const useCompleteList = (listIdentifier: number) => {
  const { getAccessTokenSilently } = useAuth0();
  const { enqueueSuccessNotification, enqueueErrorNotification } = useSaladBar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ assigneeId, listId }: { assigneeId?: string; listId: number }) => {
      const token = await getAccessTokenSilently();
      if (assigneeId) return updateAssigneeForList(token, listId, assigneeId, { isComplete: true });
      else return updateList(token, listId, { isComplete: true });
    },
    onSuccess: () => {
      enqueueSuccessNotification('List has been completed');
      queryClient.invalidateQueries({ queryKey: ['list', listIdentifier] });
    },
    onError: () => enqueueErrorNotification('List could not be completed'),
  });
};
interface Props {
  list: SupporterList | undefined;
  isOpen: boolean;
  onClose: () => void;
  userIsCoordinator: boolean;
  userIsCaller: boolean;
}

export const CompleteListDialog = ({
  list,
  isOpen,
  onClose: handleClose,
  userIsCaller,
  userIsCoordinator,
}: Props) => {
  const { user } = useAuth0();
  const { mutateAsync: completeList } = useCompleteList(list ? list.id : 0);
  const writesBlocked = useRedisWritesBlocked();
  const userRoleForList = getUserRoleForList(userIsCoordinator, userIsCaller);
  const navigate = useNavigate();

  const assigneeComplete =
    user?.sub && list
      ? list.assignedSubs.find((assignee) => assignee.assignedSubInfo.id === user.sub)
          ?.assignedSubUncalledCount === 0
      : false;

  const [completionFor, setCompletionFor] = useState<'All' | 'Me'>(
    userRoleForList === 'Coordinator' || assigneeComplete ? 'All' : 'Me'
  );

  const handleCompleteListDialogSave = async () => {
    if (!list || writesBlocked) return;
    const assigneeId = completionFor === 'Me' ? (user?.sub ?? '') : undefined;

    try {
      await completeList({ listId: list.id, assigneeId });
      if (completionFor === 'Me' && userIsCoordinator) {
        navigate(`${microfrontendURLPrefix}/lists/${list.id}/detail`);
      } else {
        navigate(`${microfrontendURLPrefix}/lists`);
      }
      handleClose();
    } catch {
      // The mutation onError already toasts. Leave the dialog open.
    }
  };

  const title = userRoleForList === 'Both' ? 'Complete this list for' : 'Complete this list?';

  return (
    <AgDialog
      isOpen={isOpen}
      onClose={handleClose}
      dialogTitle={title}
      primaryButton={{
        text: 'Complete',
        onClick: handleCompleteListDialogSave,
        disabled: writesBlocked,
      }}
      data-testid="CompleteList"
    >
      {userRoleForList === 'Both' ? (
        <Box
          sx={{
            width: "80%",
            margin: "auto",
            marginBottom: 2
          }}>
          <ToggleButtonGroup
            fullWidth
            value={completionFor}
            exclusive
            onChange={(_, value: 'All' | 'Me' | null) => {
              if (value) setCompletionFor(value);
            }}
          >
            <ToggleButton disabled={assigneeComplete} value={'Me'}>
              Me
            </ToggleButton>
            <ToggleButton value={'All'}>All</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      ) : (
        <></>
      )}

      <Typography sx={{
        marginBottom: 1
      }}>
        Are you sure you want to complete the list <strong>{list?.name}?</strong>
      </Typography>
      {(userRoleForList === 'Coordinator' ||
        (userRoleForList === 'Both' && completionFor === 'All')) && (
        <Typography>
          <em>Note: this will complete the list for all assigned users.</em>
        </Typography>
      )}
    </AgDialog>
  );
};
